import Typography from "@mui/material/Typography";

import useUpdateClips from "@app/analytics/Home/hooks/useUpdateClips";
import type { ActionItemFeedItem } from "@app/analytics/Home/types";
import { showToast } from "@app/common/hooks/useToasts";
import ROUTES from "@app/common/routes";
import type {
  ActionItemClipStatus,
  UpdateClipOperation,
} from "@app/common/types";
import useAnchorElement from "@app/utils/hooks/useAnchorElement";

import { Link } from "@rui/Link";
import Menu from "@rui/Menu";
import MenuItem from "@rui/MenuItem";
import { Row } from "@rui/Stack";

import DetailsPanelHeader from "./DetailsPanelHeader";
import DetailsPanelVideoClip from "./DetailsPanelVideoClip";
import {
  ActionsMenuButton,
  ContextAccordion,
  DetailsPanelContent,
  DetailsPanelFooter,
  Panel,
} from "./styles";
import ActionItemStatusButton from "../ActionItemStatusButton";

interface ActionItemDetailsPanelProps {
  item: ActionItemFeedItem;
  onClose: () => void;
}

const ActionItemDetailsPanel = ({
  item,
  onClose,
}: ActionItemDetailsPanelProps) => {
  const {
    anchorElement: menuAnchorEl,
    onClick: onClickMenu,
    onClose: onCloseMenu,
    open: menuOpen,
  } = useAnchorElement();

  const updateClip = useUpdateClips();

  const handleUpdateStatus = (status: ActionItemClipStatus) => {
    const operation: UpdateClipOperation = {
      type: "action_item",
      id: item.entityId,
      sessionId: item.session.id,
      status,
    };

    updateClip.mutate(operation, {
      onSuccess: () => {
        if (status === "user_reject") {
          showToast({
            severity: "success",
            message: "Your feedback has been recorded.",
          });
        }
      },
      onError: () => {
        showToast({
          severity: "error",
          message: "Something went wrong and your changes could not be saved.",
        });
      },
    });
  };

  return (
    <Panel>
      <DetailsPanelHeader item={item} onClose={onClose} />
      <DetailsPanelContent spacing={1}>
        <Typography variant="bodySecondaryReg">{item.text}</Typography>
        <Row>
          <ActionItemStatusButton
            disabled={!item.data.canEdit}
            status={item.data.status}
            onChangeStatus={handleUpdateStatus}
          />
        </Row>
        {item.videoClip && (
          <ContextAccordion defaultExpanded>
            <DetailsPanelVideoClip
              entityId={item.entityId}
              session={item.session}
              videoClip={item.videoClip}
            />
          </ContextAccordion>
        )}
      </DetailsPanelContent>
      <DetailsPanelFooter>
        <ActionsMenuButton onClick={onClickMenu} />
        <Menu open={menuOpen} anchorEl={menuAnchorEl} onClose={onCloseMenu}>
          <Link to={ROUTES.meetingReportById(item.session.id)}>
            <MenuItem>View Report</MenuItem>
          </Link>
        </Menu>
      </DetailsPanelFooter>
    </Panel>
  );
};

export default ActionItemDetailsPanel;
